import { useEffect, useState } from 'react';
import { AuthUser } from '../types/auth.types';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Kiểm tra token JWT đã hết hạn chưa
 */
function isTokenExpired(token: string): boolean {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    
    return payload.exp ? Date.now() >= payload.exp * 1000 : false;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true;
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true
  });

  // Hàm kiểm tra token hợp lệ và cập nhật trạng thái
  const validateAuth = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      // Không có token hoặc dữ liệu người dùng, đặt trạng thái về chưa xác thực
      setState(prev => ({
        ...prev,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      }));
      return;
    }

    try {
      // Kiểm tra token có hết hạn không
      if (isTokenExpired(token)) {
        // Token hết hạn, logout
        logout();
        return;
      }

      const user = JSON.parse(userStr) as AuthUser;
      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      console.error('Error parsing user data:', error);
      logout();
    }
  };

  // Chạy khi mount và khi localStorage thay đổi
  useEffect(() => {
    validateAuth();

    // Thêm event listener để kiểm tra thay đổi trong localStorage từ tab khác
    const handleStorageChange = () => {
      validateAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Kiểm tra lại mỗi khi focus vào trang
  useEffect(() => {
    const handleFocus = () => {
      validateAuth();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const login = (token: string, user: AuthUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false
    });
  };

  const logout = () => {
    // Xóa dữ liệu từ localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Cập nhật state
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  return {
    ...state,
    login,
    logout
  };
} 